"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import { LoadingLayout } from "@/components/layouts/loading-layout";
import { canAccessInternal } from "@/config/flags";
import { LINKS } from "@/config/links";
import { ConnectionDropError } from "@/lib/utils/errors";
import { useGetStartedTaskList } from "@/subsystems/access/get-started/hooks/use-get-started-tasklist";
import { useJwt } from "@/subsystems/access/json-web-token/hooks/use-jwt";
import { isApplicationOnboardingPath } from "@/subsystems/access/onboarding/lib/is-application-onboarding-path";
import { useGetEmployee, useGetEmployeeAgreements } from "@/subsystems/user/employees/hooks";
import { useGetOrganizationCompliance } from "@/subsystems/user/organizations/hooks";

import { OnboardingPermissionsContext } from "./onboarding-permissions-context";

export function OnboardingPermissionsProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const jwt = useJwt();
  const { employee } = useGetEmployee();
  const { isLoading: employeeAgreementsIsLoading, privacyPolicy, termsOfService } = useGetEmployeeAgreements();

  const { mintredeemCompliance, organizationCompliance, vendorCompliance } = useGetOrganizationCompliance();

  const productAccess = jwt?.organization?.productAccess ?? {};
  const productAccessIsLoading = !jwt?.organization;

  const complianceErrored =
    Boolean(organizationCompliance?.error) || Boolean(mintredeemCompliance?.error) || Boolean(vendorCompliance?.error);

  const permissionsAreLoading =
    employee?.isLoading ||
    employeeAgreementsIsLoading ||
    organizationCompliance?.isLoading ||
    mintredeemCompliance?.isLoading ||
    vendorCompliance?.isLoading ||
    productAccessIsLoading ||
    complianceErrored;

  // Organization-level capabilities
  // This comes off the JWT. It means they are eligible for these items, but not approved yet...
  const isMintRedeemerEligible = Boolean(productAccess?.mintredeem ?? false);
  const isVendorEligible = Boolean(productAccess?.vendor ?? false);

  // This comes off V2 endpoint to confirm compliance team has approved/rejected
  const isMintRedeemerApproved = Boolean(
    (isMintRedeemerEligible && mintredeemCompliance?.data && mintredeemCompliance?.data?.status === "approved") ??
      false,
  );

  const isVendorApproved = Boolean(
    (isVendorEligible && vendorCompliance?.data && vendorCompliance?.data?.status === "approved") ?? false,
  );

  // Base requirements
  const hasAcceptedPrivacyPolicy = Boolean(privacyPolicy?.data?.acceptanceStatus ?? false);
  const hasAcceptedTermsOfService = Boolean(termsOfService?.data?.acceptanceStatus ?? false);
  const hasPassedVerification = Boolean(isMintRedeemerApproved || isVendorApproved);
  const hasEnteredFirstName = Boolean(employee?.data?.firstName ?? false);
  const hasEnteredLastName = Boolean(employee?.data?.lastName ?? false);
  const hasEnteredFullName = Boolean(hasEnteredFirstName && hasEnteredLastName);

  // Base portal access
  const canEnterPortal = Boolean(
    hasAcceptedTermsOfService && hasAcceptedPrivacyPolicy && hasEnteredFullName && hasPassedVerification,
  );

  const needsOnboarding = !canEnterPortal;

  const onboardingRequirements = {
    hasAcceptedPrivacyPolicy,
    hasAcceptedTermsOfService,
    hasPassedVerification,
    hasEnteredFullName,
  };

  const isInternalApproved = canAccessInternal(employee?.data?.email || null);

  // Organization-level feature access
  const canEnterCustomerPortal = canEnterPortal && (isMintRedeemerApproved || isVendorApproved);
  const canEnterInternalPortal = canEnterPortal && isInternalApproved;

  // The flagged flow owns its steps under /onboarding; pushing the entry route would discard the current step.
  // The legacy pages keep the push: /onboarding/verification relies on it when name or agreements are missing.
  const isInApplicationOnboarding = isApplicationOnboardingPath(pathname);

  useEffect(() => {
    if (!permissionsAreLoading && !complianceErrored && needsOnboarding && !isInApplicationOnboarding) {
      router.push(LINKS.ONBOARDING);
    }
  }, [permissionsAreLoading, needsOnboarding, complianceErrored, isInApplicationOnboarding, router]);

  if (!permissionsAreLoading && complianceErrored) {
    throw new ConnectionDropError("Failed to load compliance data");
  }

  if (permissionsAreLoading) {
    return <LoadingLayout />;
  }

  return (
    <OnboardingPermissionsContext
      value={{
        canEnterCustomerPortal,
        canEnterInternalPortal,
        canEnterPortal,
        hasAcceptedPrivacyPolicy,
        hasAcceptedTermsOfService,
        hasPassedVerification,
        hasEnteredFirstName,
        hasEnteredFullName,
        hasEnteredLastName,
        isMintRedeemerApproved,
        isMintRedeemerEligible,
        isVendorApproved,
        isVendorEligible,
        needsOnboarding,
        onboardingRequirements,
      }}
    >
      {canEnterCustomerPortal ? <GetStartedLoadingGate>{children}</GetStartedLoadingGate> : children}
    </OnboardingPermissionsContext>
  );
}

/**
 * Defers rendering children until the get-started task list has loaded.
 * Mounted only when the user has passed compliance verification, so the
 * underlying queries (wire-details, customer-addresses, networks) never
 * fire for unverified users on the onboarding/Sumsub page.
 */
function GetStartedLoadingGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useGetStartedTaskList();

  if (isLoading) {
    return <LoadingLayout />;
  }

  return <>{children}</>;
}
